import { Suspense, lazy } from "react";
import { Navigate, Outlet } from "react-router-dom";
import AuthGuard from "../guards/AuthGuard";
import RoleGuard from "@/guards/RoleGuard";
import DashboardLayout from "../layouts/dashboard";
import {
  PATH_SETTINGS,
  PATH_SETTINGS_AVAILABILITY,
  PATH_SETTINGS_BILLING,
  PATH_SETTINGS_CALL_CENTER,
  PATH_SETTINGS_PHONE_NUMBERS,
  PATH_SETTINGS_CHANNELS,
  PATH_SETTINGS_DOMAIN_CONNECT,
  PATH_SETTINGS_DOMAINS,
  PATH_SETTINGS_EMAIL_TEMPLATES,
  PATH_SETTINGS_EMAIL_TEMPLATE_EDIT,
  PATH_SETTINGS_SNIPPETS,
  PATH_SETTINGS_DOCUMENTS,
  PATH_SETTINGS_DOCUMENT_PREVIEW,
  PATH_SETTINGS_FIELDS,
  PATH_SETTINGS_INTEGRATIONS,
  PATH_SETTINGS_INTEGRATIONS_GOOGLE_ADS,
  PATH_SETTINGS_INTEGRATIONS_SLACK,
  PATH_SETTINGS_INTEGRATIONS_HUBSPOT,
  PATH_SETTINGS_INTEGRATIONS_META,
  PATH_SETTINGS_PIXEL_INSTALL,
  PATH_SETTINGS_PROFILE,
  PATH_SETTINGS_TRAINING,
  PATH_SETTINGS_USER_MANAGEMENT,
  PATH_SETTINGS_EMAIL,
} from "./paths";
import { getRelativePath } from "@/utils/paths";
import Loader from "@/components/Loader";
import { IS_PRODUCTION } from "@/config";

const Settings = lazy(() => import("@/pages/settings/settings"));
const ProfileSettings = lazy(() => import("@/pages/settings/profile"));
const AvailabilitySettings = lazy(() => import("@/pages/settings/availability"));
const ProfileUserManagement = lazy(() => import("@/pages/settings/user-management"));
const ProfileBilling = lazy(() => import("@/pages/settings/billing"));
const IntegrationsContainer = lazy(() => import("@/pages/settings/integrations/integrations.container"));
const HubspotIntegration = lazy(() => import("@/pages/settings/integrations/hubspot/hubspot.settings.container"));
const MetaIntegration = lazy(() => import("@/pages/settings/integrations/meta/meta.settings.container"));
const GoogleAdsSettings = lazy(() => import("@/pages/settings/integrations/google-ads/google.ads.settings.container"));
const SlackSettings = lazy(() => import("@/pages/settings/integrations/slack/slack.settings.container"));
const PixelInstallContainer = lazy(() => import("@/pages/settings/pixel-install"));
const DomainsContainer = lazy(() => import("@/pages/settings/domains"));
const DomainConnect = lazy(() => import("@/pages/settings/domains/connect"));
const FieldsContainer = lazy(() => import("@/pages/settings/fields"));
const CallCenterSettings = lazy(() => import("@/pages/settings/call-center"));
const PhoneNumbersSettings = lazy(() => import("@/pages/settings/phone-numbers"));
const ChannelsSettings = lazy(() => import("@/pages/settings/channels"));
const TrainingSettings = lazy(() => import("@/pages/settings/training"));
const EmailTemplatesSettings = lazy(() => import("@/pages/settings/library/email-templates/email-templates-container").then((m) => ({ default: m.EmailTemplatesContainer })));
const EmailTemplateEditor = lazy(() => import("@/pages/settings/library/email-templates/email-template-editor-container").then((m) => ({ default: m.EmailTemplateEditorContainer })));
const SnippetsSettings = lazy(() => import("@/pages/settings/library/snippets-settings-tab").then((m) => ({ default: m.SnippetsSettingsTab })));
const DocumentsSettings = lazy(() => import("@/pages/settings/library/documents/documents-settings-tab").then((m) => ({ default: m.DocumentsSettingsTab })));
const DocumentPreview = lazy(() => import("@/pages/documents/viewer/document-preview.container").then((m) => ({ default: m.DocumentPreviewContainer })));

const SettingsRoot = (
  <AuthGuard>
    <RoleGuard>
      <DashboardLayout>
        <Suspense fallback={<Loader />}>
          <Outlet />
        </Suspense>
      </DashboardLayout>
    </RoleGuard>
  </AuthGuard>
);

const settingsRoutes = [
  {
    path: PATH_SETTINGS,
    element: SettingsRoot,
    children: [
      {
        index: true,
        element: <Navigate to={PATH_SETTINGS_PROFILE} replace />,
      },
      {
        path: getRelativePath(PATH_SETTINGS, PATH_SETTINGS_PROFILE),
        element: (
          <Settings>
            <ProfileSettings />
          </Settings>
        ),
      },
      {
        path: getRelativePath(PATH_SETTINGS, PATH_SETTINGS_AVAILABILITY),
        element: (
          <Settings>
            <AvailabilitySettings />
          </Settings>
        ),
      },
      {
        path: getRelativePath(PATH_SETTINGS, PATH_SETTINGS_USER_MANAGEMENT),
        element: (
          <Settings>
            <ProfileUserManagement />
          </Settings>
        ),
      },
      {
        path: getRelativePath(PATH_SETTINGS, PATH_SETTINGS_INTEGRATIONS),
        element: (
          <Settings>
            <IntegrationsContainer />
          </Settings>
        ),
      },
      {
        path: getRelativePath(PATH_SETTINGS, PATH_SETTINGS_BILLING),
        element: (
          <Settings>
            <ProfileBilling />
          </Settings>
        ),
      },
      {
        path: getRelativePath(PATH_SETTINGS, PATH_SETTINGS_PIXEL_INSTALL),
        element: (
          <Settings>
            <PixelInstallContainer />
          </Settings>
        ),
      },
      {
        path: getRelativePath(PATH_SETTINGS, PATH_SETTINGS_DOMAINS),
        element: (
          <Settings>
            <DomainsContainer />
          </Settings>
        ),
      },
      {
        path: getRelativePath(PATH_SETTINGS, PATH_SETTINGS_DOMAIN_CONNECT),
        element: <DomainConnect />,
      },
      {
        path: getRelativePath(PATH_SETTINGS, PATH_SETTINGS_FIELDS),
        element: (
          <Settings>
            <FieldsContainer />
          </Settings>
        ),
      },
      {
        path: getRelativePath(PATH_SETTINGS, PATH_SETTINGS_CALL_CENTER),
        element: (
          <Settings>
            <CallCenterSettings />
          </Settings>
        ),
      },
      {
        path: getRelativePath(PATH_SETTINGS, PATH_SETTINGS_PHONE_NUMBERS),
        element: (
          <Settings>
            <PhoneNumbersSettings />
          </Settings>
        ),
      },
      // Settings → Channels (IR-68) is not registered in production builds —
      // the Inbox/omnichannel module is not ready for customers yet, so the
      // URL falls through to the global 404; settings.tsx hides the nav item.
      ...(IS_PRODUCTION
        ? []
        : [
            {
              path: getRelativePath(PATH_SETTINGS, PATH_SETTINGS_CHANNELS),
              element: (
                <Settings>
                  <ChannelsSettings />
                </Settings>
              ),
            },
          ]),
      {
        path: getRelativePath(PATH_SETTINGS, PATH_SETTINGS_TRAINING),
        element: (
          <Settings>
            <TrainingSettings />
          </Settings>
        ),
      },
      {
        // Email settings now live in a tab of the user-level profile page.
        path: getRelativePath(PATH_SETTINGS, PATH_SETTINGS_EMAIL),
        element: <Navigate to={`${PATH_SETTINGS_PROFILE}?tab=email`} replace />,
      },
      {
        path: getRelativePath(PATH_SETTINGS, PATH_SETTINGS_EMAIL_TEMPLATES),
        element: (
          <Settings>
            <EmailTemplatesSettings />
          </Settings>
        ),
      },
      {
        path: getRelativePath(PATH_SETTINGS, PATH_SETTINGS_SNIPPETS),
        element: (
          <Settings>
            <SnippetsSettings />
          </Settings>
        ),
      },
      // Settings → Documents (IR-390) is not registered in production builds —
      // module not ready for customers; the URL falls through to the global
      // 404 and settings.tsx hides the nav item (preview route gated below).
      ...(IS_PRODUCTION
        ? []
        : [
            {
              path: getRelativePath(PATH_SETTINGS, PATH_SETTINGS_DOCUMENTS),
              element: (
                <Settings>
                  <DocumentsSettings />
                </Settings>
              ),
            },
          ]),
      {
        path: getRelativePath(PATH_SETTINGS, PATH_SETTINGS_INTEGRATIONS_HUBSPOT),
        element: <HubspotIntegration />,
      },
      {
        path: getRelativePath(PATH_SETTINGS, PATH_SETTINGS_INTEGRATIONS_META),
        element: <MetaIntegration />,
      },
      {
        path: getRelativePath(PATH_SETTINGS, PATH_SETTINGS_INTEGRATIONS_GOOGLE_ADS),
        element: <GoogleAdsSettings />,
      },
      {
        path: getRelativePath(PATH_SETTINGS, PATH_SETTINGS_INTEGRATIONS_SLACK),
        element: <SlackSettings />,
      },
    ],
  },
  // Full screen, no dashboard chrome — same guard stack as the rest of
  // Settings (AuthGuard + RoleGuard), minus DashboardLayout. Same pattern as
  // onboardingRoutes' chromeless wizard.
  {
    path: PATH_SETTINGS_EMAIL_TEMPLATE_EDIT,
    element: (
      <AuthGuard>
        <RoleGuard>
          <Suspense fallback={<Loader />}>
            <EmailTemplateEditor />
          </Suspense>
        </RoleGuard>
      </AuthGuard>
    ),
  },
  // The library's preview of a document, opened in a new tab: the hosted
  // page's own chrome, without the email gate and without counting a view.
  // Skipped in production builds together with Settings → Documents (IR-390).
  ...(IS_PRODUCTION
    ? []
    : [
        {
          path: PATH_SETTINGS_DOCUMENT_PREVIEW,
          element: (
            <AuthGuard>
              <RoleGuard>
                <Suspense fallback={<Loader />}>
                  <DocumentPreview />
                </Suspense>
              </RoleGuard>
            </AuthGuard>
          ),
        },
      ]),
];

export default settingsRoutes;